import { create } from 'zustand';

import ReduxStore from '@/shared/redux/store';
import { getSessionCorrelationId } from '@/shared/services/helpers/auth-helper/auth-helper.service';

interface ApiCallRecord {
  endpoint: string;
  method: string;
  status: number | null;
  timestamp: string;
}

export interface CapturedContext {
  recentFailures: ApiCallRecord[];
  failedCall: ApiCallRecord;
  sessionId: string;
  userId: string;
  route: string;
}

interface BugReportState {
  capturedContext: CapturedContext | null;
  hasCapture: boolean;
  lastCaptureAt: number | null;
  lastSubmittedKey: string | null;
  capture: (meta: { endpoint: string; method: string; status: number | null }) => void;
  flush: () => CapturedContext | null;
  discardCapture: () => void;
  setSubmittedKey: (key: string) => void;
  clear: () => void;
}

const RING_BUFFER_SIZE = 3;
const RATE_LIMIT_MS = 30_000;

export const useBugReportStore = create<BugReportState>((set, get) => ({
  capturedContext: null,
  hasCapture: false,
  lastCaptureAt: null,
  lastSubmittedKey: null,

  capture: ({ endpoint, method, status }) => {
    const { lastCaptureAt, lastSubmittedKey, capturedContext } = get();
    const now = Date.now();

    // Rate-limit: drop if within 30s of last accepted capture
    if (lastCaptureAt !== null && now - lastCaptureAt < RATE_LIMIT_MS) return;

    // Dedup: same error already submitted this session
    const key = `${method.toUpperCase()}:${endpoint}:${status ?? 'network'}`;
    if (key === lastSubmittedKey) return;

    const record: ApiCallRecord = {
      endpoint,
      method: method.toUpperCase(),
      status,
      timestamp: new Date().toISOString()
    };

    // Ring buffer: update timestamp instead of adding duplicate endpoint+status
    const existing = capturedContext?.recentFailures ?? [];
    const dupeIdx = existing.findIndex((r) => r.endpoint === endpoint && r.status === status);
    const recentFailures =
      dupeIdx >= 0
        ? existing.map((r, i) => (i === dupeIdx ? { ...r, timestamp: record.timestamp } : r))
        : [...existing.slice(-(RING_BUFFER_SIZE - 1)), record];

    set({
      capturedContext: {
        recentFailures,
        failedCall: record,
        sessionId: getSessionCorrelationId(),
        userId: ReduxStore.getState().auth.userId ?? '',
        route: window.location.pathname
      },
      hasCapture: true,
      lastCaptureAt: now
    });
  },

  flush: () => {
    const { capturedContext } = get();
    set({ capturedContext: null, hasCapture: false });
    return capturedContext;
  },

  discardCapture: () => set({ capturedContext: null, hasCapture: false }),

  setSubmittedKey: (key) => set({ lastSubmittedKey: key }),

  clear: () => set({ capturedContext: null, hasCapture: false, lastSubmittedKey: null })
}));
